// /help — how Valor Web works, and the game's Terms of Service as the server sends them (HelpController).
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Page } from "@/app/Page";
import { Card, CardHead, Empty, Skeleton } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { useHelp } from "@/state/socialQueries";
import "../social.css";

export function Help() {
  const nav = useNavigate();
  const session = useSession();
  const q = useHelp();
  const info = useQuery({ queryKey: ["session-version"], queryFn: () => api.session.get(), staleTime: Infinity });
  const parts = useMemo(() => {
    const out: { id: string; title: string; text: string }[] = [];
    const walk = (node: unknown, path: string) => {
      if (!node || typeof node !== "object") return;
      const o = node as Record<string, unknown>;
      if (typeof o.title === "string" && typeof o.text === "string") { out.push({ id: path, title: o.title, text: o.text }); return; }
      for (const [k, val] of Object.entries(o)) walk(val, path ? `${path}.${k}` : k);
    };
    walk(q.data?.help, "");
    return out;
  }, [q.data]);
  const w = session.wid ? `/w/${session.wid}` : null;

  return (
    <Page layout="col" header={{ kind: "title", title: "HELP", subtitle: "How things work" }}>
      <div className="stack">
        <Card>
          <CardHead title="Valor in the browser" aside={info.data?.version ? `build ${String(info.data.version).slice(0, 8)}` : undefined} />
          <div className="hp-part">
            <p>
              This is the same Valor as on your phone — the same worlds, cities and players — played from a browser. Nothing is separate: a city you build here is the city in the app.
              The bottom bar moves between <b className="gold">Portal</b> (every world you play), <b className="gold">Quests</b> (rewards for the city you are in), the <b className="gold">Map</b>, your <b className="gold">Guild</b> and the <b className="gold">Store</b>; opening a world takes you to your city. Inside a world the top of the screen shows your resources and the server time; <b className="gold">Reports</b> and <b className="gold">Mail</b> sit left of the city name (tap the name to go to the city, the ≡ beside it to change city — that list searches by name, coordinates, region or tag, filters by region and tag, and sorts by name, coordinates, region or tag), and the bell for <b className="gold">Announcements</b> and the menu (Leaderboards, Bookmarks, Worlds, Settings) sit on the right.
            </p>
            <p style={{ marginTop: 10 }}>
              Gold can't be bought here yet — the packs are shown, the prices aren't live. Everything else works. If a screen looks wrong, pull to refresh; the game's answers are re-read every few seconds while a screen is open.
            </p>
          </div>
          <div className="hp-toc" style={{ marginTop: 6 }}>
            {w && <button type="button" className="chipbtn" onClick={() => nav(`${w}/reports`)}>Reports</button>}
            {w && <button type="button" className="chipbtn" onClick={() => nav(`${w}/mail`)}>Mail</button>}
            {w && <button type="button" className="chipbtn" onClick={() => nav(`${w}/rank`)}>Leaderboards</button>}
            <button type="button" className="chipbtn" onClick={() => nav("/settings")}>Settings</button>
          </div>
        </Card>

        <Card>
          <CardHead title="Terms of service" aside={parts.length ? `${parts.length} parts` : undefined} />
          {q.isLoading && !q.data && <Skeleton h={200} />}
          {q.isError && <Empty>Couldn't read the terms right now.</Empty>}
          {q.data && !parts.length && <Empty>The game sent no terms.</Empty>}
          {parts.map((p) => (
            <section key={p.id} className="hp-part">
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </section>
          ))}
        </Card>
      </div>
    </Page>
  );
}
